import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ChatTheme, lightTheme, darkTheme, generateThemeCSS } from '../lib/themes/chat-themes';

interface ChatThemeContextType {
  theme: ChatTheme;
  themeName: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  // Backward compatibility
  chatTheme: ChatTheme;
  activeTheme: 'light' | 'dark';
}

const ChatThemeContext = createContext<ChatThemeContextType | undefined>(undefined);

export const useChatTheme = () => {
  const context = useContext(ChatThemeContext);
  if (!context) {
    throw new Error('useChatTheme must be used within a ChatThemeProvider');
  }
  return context;
};

interface ChatThemeProviderProps {
  children: ReactNode;
  defaultTheme?: 'light' | 'dark';
}

export const ChatThemeProvider: React.FC<ChatThemeProviderProps> = ({
  children,
  defaultTheme = 'light'
}) => {
  // Initialize theme from localStorage or use default
  const [themeName, setThemeName] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('chat-theme');
    return (saved === 'light' || saved === 'dark') ? saved : defaultTheme;
  });

  const theme = themeName === 'dark' ? darkTheme : lightTheme;

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('chat-theme', themeName);

    // Inject theme-specific CSS
    const styleId = 'chat-theme-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = generateThemeCSS(theme);

    // Add theme class to body for global theme awareness
    document.body.classList.remove('chat-theme-light', 'chat-theme-dark');
    document.body.classList.add(`chat-theme-${themeName}`);

    // Cleanup function
    return () => {
      document.body.classList.remove(`chat-theme-${themeName}`);
    };
  }, [themeName, theme]);

  const toggleTheme = () => {
    setThemeName(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setTheme = (theme: 'light' | 'dark') => {
    setThemeName(theme);
  };

  return (
    <ChatThemeContext.Provider value={{
      theme,
      themeName,
      toggleTheme,
      setTheme,
      // Backward compatibility aliases
      chatTheme: theme,
      activeTheme: themeName
    }}>
      {children}
    </ChatThemeContext.Provider>
  );
};

// Hook to sync with system theme preference
export const useSystemThemeSync = () => {
  const { setTheme } = useChatTheme();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only sync if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('chat-theme-manual');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    // Initial sync
    const savedTheme = localStorage.getItem('chat-theme-manual');
    if (!savedTheme) {
      setTheme(mediaQuery.matches ? 'dark' : 'light');
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [setTheme]);
};

// Utility hook for theme-aware styling
export const useThemeStyles = () => {
  const { theme } = useChatTheme();

  return {
    // Container styles
    container: {
      background: theme.colors.containerBackground,
      borderRadius: theme.borderRadius.container,
      boxShadow: theme.shadows.container,
      border: `1px solid ${theme.colors.borderDefault}`,
    },
    containerHover: {
      boxShadow: theme.shadows.containerHover,
      borderColor: theme.colors.borderHover,
      transform: 'translateY(-2px)',
    },

    // Header styles
    header: {
      background: theme.colors.headerBackground,
      padding: theme.spacing.headerPadding,
      borderBottom: `1px solid ${theme.colors.borderDefault}`,
      backdropFilter: 'blur(10px)',
      boxShadow: theme.shadows.header,
    },

    // Message area styles
    messageArea: {
      background: theme.colors.messagesBackground,
      padding: theme.spacing.messagePadding,
      flex: 1,
      overflowY: 'auto' as const,
      scrollBehavior: 'smooth' as const,
    },

    // Input area styles
    inputArea: {
      padding: theme.spacing.inputPadding,
      background: theme.colors.inputBackground,
      borderTop: `1px solid ${theme.colors.borderDefault}`,
      backdropFilter: 'blur(10px)',
      boxShadow: theme.shadows.input,
    },

    // Message bubble styles
    receivedBubble: {
      background: theme.colors.receivedBubbleBackground,
      color: theme.colors.receivedBubbleText,
      borderRadius: theme.borderRadius.bubble,
      padding: '14px 18px',
      maxWidth: '70%',
      boxShadow: theme.shadows.receivedBubble,
      border: `1px solid ${theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.04)'}`,
    },
    sentBubble: {
      background: theme.colors.sentBubbleBackground,
      color: theme.colors.sentBubbleText,
      borderRadius: theme.borderRadius.bubble,
      padding: '14px 18px',
      maxWidth: '70%',
      boxShadow: theme.shadows.sentBubble,
      border: `1px solid ${theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(67, 97, 238, 0.3)'}`,
    },

    // Typography styles
    messageText: {
      fontSize: theme.typography.messageFontSize,
      lineHeight: 1.5,
    },
    timeText: {
      fontSize: theme.typography.timeFontSize,
      opacity: theme.name === 'dark' ? 0.7 : 0.6,
      marginTop: '6px',
      display: 'block',
    },

    // Avatar styles
    avatar: {
      width: '48px',
      height: '48px',
      background: theme.colors.accent,
      borderRadius: theme.borderRadius.avatar,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: '18px',
      color: 'white',
      boxShadow: theme.shadows.avatar,
    },

    // Status dot styles
    statusDot: {
      width: '8px',
      height: '8px',
      background: theme.colors.statusDot,
      borderRadius: '50%',
      boxShadow: theme.shadows.statusDot,
    },
  };
};